import React from "react";

const safeStr = (val) => {
  if (val === null || val === undefined) return "";
  return String(val).trim();
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const parts = String(dateStr).split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

const numberToWords = (num) => {
  if (num === null || num === undefined || num === "" || isNaN(num)) return "";

  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
  ];

  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];

  const convertBelowThousand = (n) => {
    if (n === 0) return "";
    if (n < 20) return ones[n];
    const unit = n % 10;
    const ten = Math.floor(n / 10);
    return tens[ten] + (unit ? " " + ones[unit] : "");
  };

  let amount = parseFloat(num);
  let rupees = Math.floor(amount);
  let paise = Math.round((amount - rupees) * 100);

  let words = "";

  if (rupees === 0) {
    words = "Zero Rupees";
  } else {
    let parts = [];

    // Crores (1,00,00,000)
    if (rupees >= 10000000) {
      const crore = Math.floor(rupees / 10000000);
      parts.push(convertBelowThousand(crore) + " Crore");
      rupees %= 10000000;
    }

    // Lakhs (1,00,000)
    if (rupees >= 100000) {
      const lakh = Math.floor(rupees / 100000);
      parts.push(convertBelowThousand(lakh) + " Lakh");
      rupees %= 100000;
    }

    // Thousands (1,000)
    if (rupees >= 1000) {
      const thousand = Math.floor(rupees / 1000);
      parts.push(convertBelowThousand(thousand) + " Thousand");
      rupees %= 1000;
    }

    // Hundreds (100)
    if (rupees >= 100) {
      const hundred = Math.floor(rupees / 100);
      parts.push(convertBelowThousand(hundred) + " Hundred");
      rupees %= 100;
    }

    // Ones/Tens
    if (rupees > 0) {
      parts.push(convertBelowThousand(rupees));
    }

    words = parts.join(" ") + " Rupees";
  }

  if (paise > 0) {
    words += " and " + convertBelowThousand(paise) + " Paise";
  }

  return words + " Only";
};

const PrintableTransactionReceipt = ({ form, accountHeadName, paymentModeName, transactionId, schoolDetails }) => {
  const userData = sessionStorage.getItem('user');
  const userObj = userData ? JSON.parse(userData) : {};

  const schoolName = safeStr(userObj?.school_name || schoolDetails?.school_name || "St. Isaac Advent High School");
  const branchName = safeStr(userObj?.branch || schoolDetails?.branch || "");

  let headerText = schoolName;
  if (branchName && !schoolName.toLowerCase().includes(branchName.toLowerCase())) {
    headerText = `${schoolName} (${branchName})`;
  }

  const voucherTitle = "PAYMENT VOUCHER";
  const accountLabel = "Head of Account Debited:";

  return (
    <div
      style={{
        width: "95%",
        margin: "0 auto",
        fontFamily: "Arial, sans-serif",
        color: "#000",
        padding: "10px 5px",
        boxSizing: "border-box"
      }}
    >
      {/* Header section matching template layout */}
      <div style={{ textAlign: "center", marginBottom: "8px" }}>
        <h1 style={{ margin: "0 0 2px 0", fontSize: "18px", fontWeight: "bold", letterSpacing: "0.5px" }}>
          {headerText.toUpperCase()}
        </h1>
      </div>

      {/* Double line / thick border divider */}
      <div style={{ borderBottom: "3px double #000", marginBottom: "10px" }} />

      {/* Voucher Title */}
      <h2 style={{ textAlign: "center", fontSize: "14px", fontWeight: "bold", textDecoration: "underline", margin: "0 0 12px 0" }}>
        {voucherTitle}
      </h2>

      {/* Voucher metadata row */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "15px", fontSize: "12px" }}>
        <div>
          <strong>Vr. No.:</strong>&nbsp;
          <span style={{ borderBottom: "1px dashed #000", paddingBottom: "2px", minWidth: "100px", display: "inline-block" }}>
            {transactionId || "New"}
          </span>
        </div>
        <div>
          <strong>Voucher Type:</strong>&nbsp;
          <span style={{ borderBottom: "1px dashed #000", paddingBottom: "2px", minWidth: "100px", display: "inline-block", textAlign: "center", textTransform: "capitalize" }}>
            {String(form?.txn_type || "").toLowerCase()}
          </span>
        </div>
        <div>
          <strong>Date:</strong>&nbsp;
          <span style={{ borderBottom: "1px dashed #000", paddingBottom: "2px", minWidth: "100px", display: "inline-block", textAlign: "center" }}>
            {formatDate(form?.transaction_date)}
          </span>
        </div>
      </div>

      {/* Main voucher fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>

        {/* Head of Account */}
        <div style={{ display: "flex", alignItems: "flex-end", fontSize: "12px" }}>
          <span style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>{accountLabel}</span>
          <div style={{ flexGrow: 1, borderBottom: "1px dashed #000", marginLeft: "8px", paddingBottom: "2px", fontWeight: "500" }}>
            {accountHeadName || ""} {form?.account_head_code ? `(${form.account_head_code})` : ""}
          </div>
        </div>

        {/* Name of the Recipient */}
        <div style={{ display: "flex", alignItems: "flex-end", fontSize: "12px" }}>
          <span style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Name of the Recipient:</span>
          <div style={{ flexGrow: 1, borderBottom: "1px dashed #000", marginLeft: "8px", minHeight: "18px" }}>
            {/* Blank for manual signatures/recipients as on physical paper receipt template */}
          </div>
        </div>

        {/* Amount in words */}
        <div style={{ display: "flex", alignItems: "flex-end", fontSize: "12px" }}>
          <span style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>The sum of Rupees:</span>
          <div style={{ flexGrow: 1, borderBottom: "1px dashed #000", marginLeft: "8px", paddingBottom: "2px", fontWeight: "500" }}>
            {numberToWords(form?.amount)}
          </div>
        </div>

        {/* Towards / Description */}
        <div style={{ display: "flex", alignItems: "flex-end", fontSize: "12px" }}>
          <span style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Towards:</span>
          <div style={{ flexGrow: 1, borderBottom: "1px dashed #000", marginLeft: "8px", paddingBottom: "2px", fontWeight: "500" }}>
            {form?.description || "-"}
          </div>
        </div>

        {/* By Cash / Cheque / DD No. */}
        <div style={{ display: "flex", alignItems: "flex-end", fontSize: "12px" }}>
          <span style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>By Cash / Cheque / UPI:</span>
          <div style={{ flexGrow: 1, borderBottom: "1px dashed #000", marginLeft: "8px", paddingBottom: "2px", fontWeight: "500" }}>
            {paymentModeName || ""} {form?.reference_no ? `- Ref: ${form.reference_no}` : ""}
          </div>
        </div>

      </div>

      {/* Amount Display */}
      <div style={{ display: "flex", alignItems: "center", marginTop: "20px", fontSize: "14px" }}>
        <span style={{ fontWeight: "bold", marginRight: "10px" }}>Rs.</span>
        <span style={{
          fontWeight: "bold",
          borderBottom: "3px double #000",
          padding: "2px 15px",
          minWidth: "150px",
          textAlign: "center"
        }}>
          {form?.amount ? `${Number(form.amount).toLocaleString('en-IN')}/-` : ""}
        </span>
      </div>

      {/* Signature Section */}
      <div style={{ marginTop: "35px", display: "flex", justifyContent: "space-between", fontSize: "11px" }}>
        <div style={{ textAlign: "center", width: "30%" }}>
          <div style={{ borderTop: "1px solid #000", paddingTop: "5px" }}>
            <strong>Cashier By</strong>
          </div>
        </div>
        <div style={{ textAlign: "center", width: "30%" }}>
          <div style={{ borderTop: "1px solid #000", paddingTop: "5px" }}>
            <strong>Approved by</strong>
            <div style={{ fontSize: "10px", marginTop: "2px" }}>Principal / General Secretary</div>
          </div>
        </div>
        <div style={{ textAlign: "center", width: "30%" }}>
          <div style={{ borderTop: "1px solid #000", paddingTop: "5px" }}>
            <strong>Receiver's Signature</strong>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PrintableTransactionReceipt;
